/**
 * AdminCleanupLegacyImages — iter278
 *
 * Owner-only screen with 3 buttons:
 *   1. Dry-run → POST /api/admin/cleanup/legacy-images?dry_run=true
 *   2. Commit (disabled until dry-run succeeded + typed phrase) →
 *      POST /api/admin/cleanup/legacy-images?dry_run=false
 *   3. Hard-delete the iter277 test ad ad_9b1ea0fa497b (disabled until
 *      typed phrase) → DELETE /api/admin/ads/{ad_id}/hard-delete
 *
 * All calls use the current logged-in session via useAuth().token —
 * no copy/paste of JWT required by the operator.
 *
 * Scope is intentionally minimal: render the raw JSON response inline
 * so the operator can verify counts/samples without leaving the page.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  AlertTriangle, ShieldCheck, Trash2, RefreshCw, CheckCircle2, Loader2, Inbox,
} from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import './AdminLayout.css';

const TEST_AD_ID = 'ad_9b1ea0fa497b';
const TEST_AD_TITLE = 'كبايه رخام';
const COMMIT_PHRASE = 'CLEAN LEGACY IMAGES';
const DELETE_PHRASE = 'HARD DELETE TEST AD';

function authedFetch(token, path, init = {}) {
  const apiBase = getApiBase();
  return fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
    credentials: 'include',
  });
}

export default function AdminCleanupLegacyImages() {
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const isArabic = lang === 'ar';

  const [dry, setDry] = useState(null);
  const [dryLoading, setDryLoading] = useState(false);
  const [dryError, setDryError] = useState(null);

  const [commit, setCommit] = useState(null);
  const [commitLoading, setCommitLoading] = useState(false);
  const [commitError, setCommitError] = useState(null);
  const [commitPhrase, setCommitPhrase] = useState('');

  const [del, setDel] = useState(null);
  const [delLoading, setDelLoading] = useState(false);
  const [delError, setDelError] = useState(null);
  const [delPhrase, setDelPhrase] = useState('');

  const ownerEmail = (user?.email || '').toLowerCase();
  const isOwner = ownerEmail === 'wassla.connect@gmail.com' || user?.is_seed_admin || user?.admin_role === 'super_admin';

  const runDry = useCallback(async () => {
    if (!token) { toast.error('Not signed in'); return; }
    setDryLoading(true);
    setDryError(null);
    try {
      const r = await authedFetch(token, '/api/admin/cleanup/legacy-images?dry_run=true&sample_size=15', { method: 'POST' });
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status}${txt ? `: ${txt.slice(0, 200)}` : ''}`);
      }
      const data = await r.json();
      setDry(data);
      toast.success(isArabic ? 'تم تشغيل المعاينة' : 'Dry-run complete');
    } catch (e) {
      setDryError(e?.message || 'Failed');
      toast.error(e?.message || 'Failed');
    } finally {
      setDryLoading(false);
    }
  }, [token, isArabic]);

  const runCommit = useCallback(async () => {
    if (!token) { toast.error('Not signed in'); return; }
    if (commitPhrase !== COMMIT_PHRASE) {
      toast.error(isArabic ? `اكتب العبارة المطلوبة بالضبط: ${COMMIT_PHRASE}` : `Type the exact phrase: ${COMMIT_PHRASE}`);
      return;
    }
    setCommitLoading(true);
    setCommitError(null);
    try {
      const r = await authedFetch(token, '/api/admin/cleanup/legacy-images?dry_run=false&sample_size=10', { method: 'POST' });
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status}${txt ? `: ${txt.slice(0, 200)}` : ''}`);
      }
      const data = await r.json();
      setCommit(data);
      toast.success(isArabic ? `تم: تم تعديل ${data.committed} إعلان` : `Done: ${data.committed} ads flipped`);
    } catch (e) {
      setCommitError(e?.message || 'Failed');
      toast.error(e?.message || 'Failed');
    } finally {
      setCommitLoading(false);
    }
  }, [token, commitPhrase, isArabic]);

  const runHardDelete = useCallback(async () => {
    if (!token) { toast.error('Not signed in'); return; }
    if (delPhrase !== DELETE_PHRASE) {
      toast.error(isArabic ? `اكتب العبارة المطلوبة بالضبط: ${DELETE_PHRASE}` : `Type the exact phrase: ${DELETE_PHRASE}`);
      return;
    }
    setDelLoading(true);
    setDelError(null);
    try {
      const r = await authedFetch(token, `/api/admin/ads/${TEST_AD_ID}/hard-delete`, { method: 'DELETE' });
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status}${txt ? `: ${txt.slice(0, 200)}` : ''}`);
      }
      const data = await r.json();
      setDel(data);
      toast.success(isArabic ? 'تم حذف الإعلان التجريبي' : 'Test ad deleted');
    } catch (e) {
      setDelError(e?.message || 'Failed');
      toast.error(e?.message || 'Failed');
    } finally {
      setDelLoading(false);
    }
  }, [token, delPhrase, isArabic]);

  const canCommit = useMemo(() =>
    !!dry && dry?.mode === 'dry_run' && (dry?.matched ?? 0) > 0 && commitPhrase === COMMIT_PHRASE && !commitLoading && !commit
  , [dry, commitPhrase, commitLoading, commit]);

  const canDelete = useMemo(() =>
    !!token && delPhrase === DELETE_PHRASE && !delLoading && !del
  , [token, delPhrase, delLoading, del]);

  if (!isOwner) {
    return (
      <div data-testid="cleanup-legacy-images-403" style={{ padding: 24 }}>
        <div className="admin-card" style={{ borderInlineStart: '3px solid #ef4444', background: 'rgba(239,68,68,0.06)' }}>
          <h3 style={{ color: '#ef4444' }}>
            <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
            {isArabic ? 'هذه الأداة مخصصة لمالك المنصة فقط' : 'Owner-only tool'}
          </h3>
          <p style={{ fontSize: 13, color: 'var(--admin-text-dim)' }}>
            {isArabic
              ? 'يجب تسجيل الدخول بحساب المالك للوصول لهذه الصفحة.'
              : 'Sign in as the platform owner to access this page.'}
          </p>
          <button type="button" className="admin-btn" onClick={() => navigate('/admin/overview')} data-testid="cleanup-back-overview">
            {isArabic ? 'العودة إلى لوحة التحكم' : 'Back to dashboard'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cleanup-legacy-images-page" style={{ padding: '0 0 60px' }}>
      <div className="admin-ads-header">
        <div>
          <h1 className="admin-ads-title" data-testid="cleanup-legacy-images-title">
            <ShieldCheck size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
            {isArabic ? 'تنظيف الصور القديمة المعطوبة' : 'Legacy Broken-Image Cleanup'}
          </h1>
          <p className="admin-ads-subtitle">
            {isArabic
              ? 'تخفي الإعلانات النشطة التي تظهر بصورة رمادية لأن ملف الصورة فُقد قبل التحويل إلى Cloudinary. حالة محايدة بدون أي عقوبة على البائع.'
              : 'Hides public ads showing grey placeholders because their image file was lost before the Cloudinary migration. Neutral status. NO seller penalty.'}
          </p>
        </div>
      </div>

      {/* ── 1. Dry-run ── */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <h3 className="admin-card-title">
          1. {isArabic ? 'معاينة (بدون أي تعديل)' : 'Dry-run (zero writes)'}
        </h3>
        <p style={{ fontSize: 13, color: 'var(--admin-text-dim)', margin: '4px 0 12px' }}>
          {isArabic
            ? 'يفحص الإعلانات النشطة فقط ويعرض العدد المتوقع تعديله. لا يكتب أي شيء في قاعدة البيانات.'
            : 'Scans active ads only and reports counts + 15 samples. No DB writes.'}
        </p>
        <button
          type="button"
          className="admin-btn"
          onClick={runDry}
          disabled={dryLoading}
          data-testid="cleanup-dry-run-btn"
        >
          {dryLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {isArabic ? 'تشغيل المعاينة' : 'Run dry-run'}
        </button>
        {dryError && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: 'rgba(239,68,68,0.08)', color: '#b91c1c', fontSize: 12 }} data-testid="cleanup-dry-run-error">
            {dryError}
          </div>
        )}
        {dry && (
          <div style={{ marginTop: 12 }} data-testid="cleanup-dry-run-result">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <span className="admin-pill blue" data-testid="cleanup-scanned">
                {isArabic ? 'مفحوصة' : 'Scanned'}: {dry.scanned_active_ads}
              </span>
              <span className="admin-pill yellow" data-testid="cleanup-matched">
                {isArabic ? 'ستُخفى' : 'Will hide'}: {dry.matched}
              </span>
              <span className="admin-pill green" data-testid="cleanup-kept">
                {isArabic ? 'إعلانات Cloudinary محمية' : 'Cloudinary kept'}: {dry.kept_cloudinary}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--admin-text-dim)', marginBottom: 8 }} data-testid="cleanup-safety">
              {Object.entries(dry.safety_flags || {}).map(([k, v]) => (
                <span key={k} style={{ marginInlineEnd: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle2 size={12} style={{ color: v ? '#16a34a' : '#dc2626' }} />
                  <code>{k}</code>
                </span>
              ))}
            </div>
            {(dry.samples || []).length > 0 && (
              <details open style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  {isArabic ? `عينة (أول ${dry.samples.length})` : `Samples (first ${dry.samples.length})`}
                </summary>
                <ol style={{ margin: '8px 0', paddingInlineStart: 20, fontSize: 12, lineHeight: 1.6 }}>
                  {dry.samples.map((s) => (
                    <li key={s.ad_id} data-testid={`cleanup-sample-${s.ad_id}`}>
                      <code>{s.ad_id}</code> — {s.title || '(no title)'} <span style={{ color: 'var(--admin-text-dim)' }}>· {s.first_image?.slice(0, 80)}</span>
                    </li>
                  ))}
                </ol>
              </details>
            )}
          </div>
        )}
      </div>

      {/* ── 2. Commit ── */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <h3 className="admin-card-title">
          2. {isArabic ? 'تنفيذ التنظيف' : 'Commit cleanup'}
        </h3>
        <p style={{ fontSize: 13, color: 'var(--admin-text-dim)', margin: '4px 0 12px' }}>
          {isArabic
            ? `يغير حالة الإعلانات المطابقة إلى "بحاجة لإعادة رفع الصورة". لا يحذف أي إعلان. لا توجد عقوبات على البائعين.`
            : `Flips matched ads to status "needs_photo_reupload". No deletions. NO seller penalties.`}
          <br />
          {isArabic ? 'اكتب العبارة التالية للتأكيد:' : 'Type the exact phrase to confirm:'} <code>{COMMIT_PHRASE}</code>
        </p>
        <input
          type="text"
          value={commitPhrase}
          onChange={(e) => setCommitPhrase(e.target.value)}
          placeholder={COMMIT_PHRASE}
          disabled={!dry || commitLoading || commit}
          style={{ width: '100%', maxWidth: 280, padding: 8, fontSize: 13, marginBottom: 10, border: '1px solid var(--admin-border)', borderRadius: 6 }}
          data-testid="cleanup-commit-phrase"
        />
        <div>
          <button
            type="button"
            className="admin-btn danger"
            onClick={runCommit}
            disabled={!canCommit}
            data-testid="cleanup-commit-btn"
          >
            {commitLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {isArabic ? 'تنفيذ التنظيف' : 'Commit cleanup'}
          </button>
        </div>
        {commitError && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: 'rgba(239,68,68,0.08)', color: '#b91c1c', fontSize: 12 }} data-testid="cleanup-commit-error">
            {commitError}
          </div>
        )}
        {commit && (
          <div style={{ marginTop: 12 }} data-testid="cleanup-commit-result">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <span className="admin-pill green" data-testid="cleanup-committed-count">
                {isArabic ? 'تم تعديل' : 'Committed'}: {commit.committed}
              </span>
              <span className="admin-pill blue">
                {isArabic ? 'سجل المراجعة مكتوب' : 'Audit logged'}: {String(commit.audit_logged)}
              </span>
            </div>
            <p style={{ fontSize: 13, marginTop: 8 }}>
              {isArabic ? commit.next_step : commit.next_step}
            </p>
          </div>
        )}
      </div>

      {/* ── 3. Hard-delete test ad ── */}
      <div className="admin-card" style={{ marginBottom: 16, borderInlineStart: '3px solid #dc2626' }}>
        <h3 className="admin-card-title">
          3. {isArabic ? 'حذف الإعلان التجريبي نهائياً' : 'Hard-delete the test ad'}
        </h3>
        <p style={{ fontSize: 13, color: 'var(--admin-text-dim)', margin: '4px 0 12px' }}>
          {isArabic
            ? `يحذف نهائياً الإعلان التجريبي الذي أُنشئ للتحقق من تكامل Cloudinary. نسخة احتياطية في ads_hard_delete_backups.`
            : `Permanently deletes the iter277 Cloudinary persistence test ad. A backup is kept in ads_hard_delete_backups for safety.`}
        </p>
        <p style={{ fontSize: 12, fontFamily: 'monospace', marginBottom: 8 }}>
          {TEST_AD_ID} — &quot;{TEST_AD_TITLE}&quot;
        </p>
        <p style={{ fontSize: 12, color: 'var(--admin-text-dim)', marginBottom: 8 }}>
          {isArabic ? 'اكتب العبارة التالية للتأكيد:' : 'Type the exact phrase to confirm:'} <code>{DELETE_PHRASE}</code>
        </p>
        <input
          type="text"
          value={delPhrase}
          onChange={(e) => setDelPhrase(e.target.value)}
          placeholder={DELETE_PHRASE}
          disabled={delLoading || del}
          style={{ width: '100%', maxWidth: 280, padding: 8, fontSize: 13, marginBottom: 10, border: '1px solid var(--admin-border)', borderRadius: 6 }}
          data-testid="cleanup-delete-phrase"
        />
        <div>
          <button
            type="button"
            className="admin-btn danger"
            onClick={runHardDelete}
            disabled={!canDelete}
            data-testid="cleanup-delete-btn"
          >
            {delLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {isArabic ? 'حذف نهائياً' : 'Hard delete'}
          </button>
        </div>
        {delError && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: 'rgba(239,68,68,0.08)', color: '#b91c1c', fontSize: 12 }} data-testid="cleanup-delete-error">
            {delError}
          </div>
        )}
        {del && (
          <div style={{ marginTop: 12, fontSize: 13 }} data-testid="cleanup-delete-result">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#16a34a', marginBottom: 4 }}>
              <CheckCircle2 size={14} />
              <strong>{isArabic ? 'تم الحذف' : 'Deleted'}</strong>
            </div>
            <div style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>
              <code>{del.ad_id}</code> — {isArabic ? 'نسخة احتياطية في' : 'backup in'} <code>{del.backed_up_to}</code>
            </div>
          </div>
        )}
      </div>

      <div className="admin-card" style={{ background: 'rgba(59,130,246,0.05)', borderInlineStart: '3px solid #3b82f6' }}>
        <p style={{ fontSize: 13, margin: 0 }}>
          {isArabic
            ? 'بعد إكمال الخطوات الثلاث، تحقق من الصفحة الرئيسية والمتجر للتأكد من اختفاء البطاقات الرمادية. الإعلانات الجديدة على Cloudinary تظل ظاهرة.'
            : 'After all three steps complete, verify Home + Shop have no grey placeholders. New Cloudinary ads remain visible.'}
        </p>
      </div>
    </div>
  );
}
